import React, { useEffect, useState } from "react";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { setPasswordApi } from "utils/authThunk";

// Helper functions
const hasNumber = (value) => /[0-9]/.test(value);
const hasMixed = (value) => /[a-z]/.test(value) && /[A-Z]/.test(value);
const hasSpecial = (value) => /[!#@$%^&*)(+=._-]/.test(value);

const strengthColor = (count) => {
  if (count < 1) return "poor";
  if (count < 2) return "weak";
  if (count < 3) return "strong";
  if (count < 4) return "heavy";
  return "poor";
};

const SetPassword = () => {
  const route = all_routes;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState(0);
  const [strength, setStrength] = useState("");
  const [eye, setEye] = useState(true);
  const [eyeConfirmPassword, setEyeConfirmPassword] = useState(true);

  const email = localStorage.getItem("email");
  const currentOtp = localStorage.getItem("currentOtp");

  useEffect(() => {
    if (password) {
      const level = strengthIndicator(password);
      const color = strengthColor(level);
      setStrength(color);
    } else {
      setStrength("");
    }
  }, [password]);

  const validatePassword = (value) => {
    if (!value) setValidationError(1);
    else if (value.length < 8) setValidationError(2);
    else if (!/[0-9]/.test(value)) setValidationError(3);
    else if (!/[!@#$%^&*()]/.test(value)) setValidationError(4);
    else setValidationError(5);
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    validatePassword(value);
  };

  const strengthIndicator = (value) => {
    let strengths = 0;
    if (value.length >= 8) strengths = 1;
    if (hasNumber(value)) strengths++;
    if (hasSpecial(value)) strengths++;
    if (hasMixed(value)) strengths++;
    return strengths;
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (!email || !currentOtp || !password) {
      toast.error("Something went wrong.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    const result = await dispatch(
      setPasswordApi({ email, currentOtp, newPassword: password })
    );

    if (result?.payload?.success) {
      toast.success(
        result?.payload?.message || "Password updated successfully."
      );
      localStorage.clear()
      navigate("/admin/login");
    } else {
      toast.error(result?.payload?.message || "Failed to update password.");
    }
  };

  const messages = () => {
    switch (validationError) {
      case 2:
        return (
          <span
            className="active mt-2"
            style={{ fontSize: 14, color: "#DC3545" }}
          >
            <ImageWithBasePath
              src="assets/img/icon/angry.svg"
              className="me-2"
              alt=""
            />
            Weak. Must contain at least 8 characters
          </span>
        );
      case 3:
        return (
          <span
            className="active mt-2"
            style={{ fontSize: 14, color: "#FFC107" }}
          >
            <ImageWithBasePath
              src="assets/img/icon/anguish.svg"
              className="me-2"
              alt=""
            />
            Average. Must contain at least 1 number
          </span>
        );
      case 4:
        return (
          <span
            className="active mt-2"
            style={{ fontSize: 14, color: "#0D6EFD" }}
          >
            <ImageWithBasePath
              src="assets/img/icon/smile.svg"
              className="me-2"
              alt=""
            />
            Almost. Must contain special character
          </span>
        );
      case 5:
        return (
          <span
            className="active mt-2"
            style={{ fontSize: 14, color: "#4BB543" }}
          >
            <ImageWithBasePath
              src="assets/img/icon/smile.svg"
              className="me-2"
              alt=""
            />
            Awesome! You have a secure password.
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="main-wrapper">
      <div className="login-content">
        <div className="row">
          <div className="col-md-6 login-bg d-none d-lg-flex">
            <div className="login-carousel-section mb-3">
              <div className="login-banner">
                <ImageWithBasePath
                  src="assets/img/auth/auth-1.svg"
                  className="img-fluid"
                  alt="Logo"
                />
              </div>
              <div className="mentor-course text-center">
                    <h3 className="mb-2">
                      Welcome to 
                      {" "}<span className="text-secondary">
                        AIATI in India.
                      </span>{" "}
                    </h3>
                    <p>
                      A trusted platform to manage, deliver, and track aviation learning .
                    </p>
                  </div>
            </div>
          </div>

          <div className="col-md-6 login-wrap-bg">
            <div className="login-wrapper">
              <div className="loginbox">
                <div className="w-100">
                  <div className="d-flex align-items-center justify-content-between login-header">
                    <img
                      src="/assets/img/logo/AviationLogo-08.png"
                      alt="Logo"
                      height="70"
                      style={{ height: "70px" }}
                    />
                    <Link to="/admin/login" className="link-1">
                      Back to Login
                    </Link>
                  </div>

                  <div className="topic">
                    <h1 className="fs-32 fw-bold">Set Password</h1>
                    <p className="fw-normal fs-14 mb-0">
                      Your new password must be different from previous
                      password.
                    </p>
                  </div>

                  <form className="mb-3 pb-3" onSubmit={handleSetPassword}>
                    <div className="mb-3">
                      <label className="form-label">
                        New Password <span className="text-danger">*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type={eye ? "password" : "text"}
                          className="form-control pass-input"
                          onChange={handlePasswordChange}
                          value={password}
                        />
                        <span
                          onClick={() => setEye(!eye)}
                          className={`toggle-password text-gray-7 fs-14 isax ${
                            eye ? "isax-eye-slash" : "isax-eye"
                          }`}
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            cursor: "pointer",
                            transform: "translateY(-50%)",
                          }}
                        />
                      </div>

                      <div
                        className={`password-strength d-flex mt-2 ${strength}-active`}
                      >
                        <span id="poor" className="active" />
                        <span id="weak" className="active" />
                        <span id="strong" className="active" />
                        <span id="heavy" className="active" />
                      </div>

                      <div id="passwordInfo">{messages()}</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Confirm Password <span className="text-danger">*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type={eyeConfirmPassword ? "password" : "text"}
                          className="form-control pass-input"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                        <span
                          onClick={() =>
                            setEyeConfirmPassword(!eyeConfirmPassword)
                          }
                          className={`toggle-password text-gray-7 fs-14 isax ${
                            eyeConfirmPassword ? "isax-eye-slash" : "isax-eye"
                          }`}
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            cursor: "pointer",
                            transform: "translateY(-50%)",
                          }}
                        />
                      </div>
                    </div>

                    <div className="d-grid">
                      <button
                        className="btn btn-secondary btn-lg"
                        type="submit"
                      >
                        Reset Password{" "}
                        <i className="isax isax-arrow-right-3 ms-1" />
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
